import { Modal, Button, Group, Text, Stack, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import { useAssignUserStore } from './store';
import { usePermissionData } from '@/pages/NewAdminConsole/hooks/usePermissionData';
import { usePermissionControl } from '@/pages/NewAdminConsole/hooks/usePermissionControl';
import { isCompanyLevelRole, NO_DEPARTMENT_ID } from '@/pages/NewAdminConsole/utils';

// 获取权限文本翻译
const getPermissionText = (roleCode: string, t: any) => {
  switch (roleCode) {
    case 'ROLE_COMPANY_OWNER':
      return t('ROLE_COMPANY_OWNER') || 'Company Owner';
    case 'ROLE_ADMIN':
      return t('ROLE_ADMIN') || 'Administrator';
    case 'ROLE_ADMIN_DEPARTMENT':
      return t('ROLE_ADMIN_DEPARTMENT') || 'Department Admin';
    case 'ROLE_EDITOR':
      return t('ROLE_EDITOR') || 'Editor';
    case 'ROLE_VIEWER':
      return t('ROLE_VIEWER') || 'Viewer';
    default:
      return t('ROLE_VIEWER') || 'Viewer';
  }
};

// 获取所有部门列表
const getAllDepartments = (nodes: any[], path: string[] = []): Array<{ path: string; id: string; isLeaf: boolean }> => {
  let departments: Array<{ path: string; id: string; isLeaf: boolean }> = [];

  for (const node of nodes) {
    const currentPath = node.id === 'root' ? [node.name] : [...path, node.name];
    const pathString = currentPath.join(' > ');
    const isLeaf = !node.children || node.children.length === 0;
    departments.push({ path: pathString, id: node.id, isLeaf });

    if (node.children && node.children.length > 0) {
      departments = departments.concat(getAllDepartments(node.children, currentPath));
    }
  }

  return departments;
};

// 过滤可选择的部门
const getFilteredDepartments = (
  allDepartments: Array<{ path: string; id: string; isLeaf: boolean }>,
  permissionControl: any
) => {
  if (permissionControl.isOwner || permissionControl.isAdmin) {
    return allDepartments.filter((dept) => dept.id !== 'root');
  }

  if (permissionControl.currentUserRole === 'ROLE_ADMIN_DEPARTMENT') {
    const currentUserPath = permissionControl.currentUser?.departmentPath;
    if (!currentUserPath) {
      return [];
    }

    return allDepartments.filter((dept) => {
      if (dept.id === 'root') return false;
      return dept.path.startsWith(currentUserPath);
    });
  }

  return [];
};

/* 分配用户对话框 */
export const AssigningUsersModal = () => {
  const { t } = useTranslation();
  const { organizationData, loadAssignableRoles, updateUserDepartmentAndRole, loadMembersData } = usePermissionData();
  const permissionControl = usePermissionControl();

  const {
    showAssignDialog,
    assigningUsers,
    assigningDept,
    assigningPermission,
    assignableRolesForAssign,
    onConfirmAssign,
    closeDialog,
    setAssigningDept,
    setAssigningPermission,
    setAssignableRoles,
  } = useAssignUserStore();

  // 计算过滤后的部门列表
  const filteredDepartments = useMemo(
    () => getFilteredDepartments(getAllDepartments(organizationData), permissionControl),
    [organizationData, permissionControl]
  );

  // 当对话框打开时，加载可分配角色
  useEffect(() => {
    if (showAssignDialog) {
      const loadRoles = async () => {
        try {
          const loader = loadAssignableRoles;
          if (loader) {
            const assignableRoles = await loader();
            setAssignableRoles(assignableRoles);

            // 单个用户时回显其当前权限
            if (assigningUsers.length === 1) {
              const currentRoleCode = assigningUsers[0]?.roleCode;
              const matched = assignableRoles.find((r) => r.code === currentRoleCode);
              if (matched) {
                setAssigningPermission(matched.id.toString());
              }
            }
          }
        } catch (error) {
          console.error('加载可分配角色失败:', error);
          setAssignableRoles([]);
        }
      };
      loadRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssignDialog]);

  // 当用户列表更新时，如果用户已有部门，设置为当前部门路径
  useEffect(() => {
    if (assigningUsers && assigningUsers.length > 0 && showAssignDialog) {
      if (
        assigningUsers[0].departmentPath &&
        assigningUsers[0].departmentPath !== t('adminConsole.newConsole.unassigned')
      ) {
        setAssigningDept(assigningUsers[0].departmentPath);
      }
    }
  }, [assigningUsers, showAssignDialog, setAssigningDept, t]);

  // 当前要分配的角色：未选择时回退为查看者
  const effectiveAssignRole =
    assignableRolesForAssign.find((r) => r.id.toString() === assigningPermission) ||
    assignableRolesForAssign.find((r) => r.code === 'ROLE_VIEWER');
  // 公司级角色（公司所有者 / 公司管理员）允许不选部门
  const departmentOptional = isCompanyLevelRole(effectiveAssignRole?.code);

  // 处理确认分配
  const handleConfirmAssign = async () => {
    // 仅公司级角色可不选部门，其余角色必须选择部门
    if (!assigningDept && !departmentOptional) return;

    const deptName = assigningDept
      ? assigningDept.split(' > ').pop() || assigningDept
      : t('adminConsole.newConsole.unassigned');
    const selectedRole = assignableRolesForAssign.find((r) => r.id.toString() === assigningPermission);
    const defaultViewerRole = assignableRolesForAssign.find((r) => r.code === 'ROLE_VIEWER');

    // 从assigningDept路径找到对应的departmentId；未选部门且为公司级角色时传 0
    const department = filteredDepartments.find((d) => d.path === assigningDept);
    const departmentId = department ? department.id : departmentOptional ? NO_DEPARTMENT_ID : '';
    const sysRoleId = selectedRole ? selectedRole.id : defaultViewerRole?.id || 1;

    const newMembers = assigningUsers.map((assigningUser) => ({
      id: assigningUser.id,
      name: assigningUser.name,
      email: assigningUser.email,
      avatar: assigningUser.name.charAt(0),
      department: deptName,
      departmentPath: assigningDept,
      departmentId: departmentId,
      sysRoleId: sysRoleId,
      permissionText: selectedRole
        ? getPermissionText(selectedRole.code, t)
        : getPermissionText(defaultViewerRole?.code || 'ROLE_VIEWER', t),
      status: t('adminConsole.newConsole.member.status.enabled'),
      joinDate: assigningUser.createdAt,
      updateAt: t('adminConsole.newConsole.member.neverLoggedIn'),
      apiKeyBound: false,
      apiKeys: [],
    }));

    try {
      // 调用外部回调（如果提供）
      if (onConfirmAssign) {
        await onConfirmAssign(newMembers);
      }

      // 使用 usePermissionData 的方法更新用户
      if (updateUserDepartmentAndRole) {
        const success = await updateUserDepartmentAndRole(
          newMembers.map((user) => user.id.toString()),
          departmentId,
          sysRoleId.toString()
        );

        if (success) {
          // 显示成功提示
          const userName = assigningUsers.map((user) => user.name).join(',');
          notifications.show({
            title: t('adminConsole.newConsole.member.assignSuccess'),
            message: t('adminConsole.newConsole.member.assignSuccessMessage', {
              userName: userName.length > 50 ? userName.slice(0, 50) : userName,
              deptName: deptName,
            }),
            color: 'green',
          });

          closeDialog();
          // 延迟刷新数据
          setTimeout(() => {
            loadMembersData(false);
          }, 200);
        }
      }
    } catch (err) {
      console.error(`分配用户出错：${err}`);
      notifications.show({
        message: t('adminConsole.newConsole.member.assignFailed'),
        color: 'red',
      });
    }
  };

  return (
    <Modal opened={showAssignDialog} onClose={closeDialog} title={t('adminConsole.newConsole.member.assignTitle')}>
      {assigningUsers && assigningUsers.length > 0 ? (
        <Stack>
          <Text>
            {t('adminConsole.newConsole.member.assignTitle')}: {assigningUsers.map((user) => user.name).join(', ')}
          </Text>

          <Select
            label={t('adminConsole.newConsole.member.department')}
            placeholder={t('adminConsole.newConsole.member.selectDepartment')}
            value={assigningDept || ''}
            onChange={(value) => {
              setAssigningDept(value || '');
            }}
            data={filteredDepartments.map((d) => ({
              value: d.path,
              label: d.path,
            }))}
            searchable
            clearable={departmentOptional}
            required={!departmentOptional}
          />

          <Select
            label={t('adminConsole.newConsole.member.permissionLevel')}
            value={assigningPermission}
            onChange={(value) => {
              const defaultRoleId = assignableRolesForAssign.find((r) => r.code === 'ROLE_VIEWER')?.id.toString() || '';
              setAssigningPermission(value || defaultRoleId);
            }}
            data={assignableRolesForAssign.map((role) => ({
              value: role.id.toString(),
              label: getPermissionText(role.code, t),
            }))}
          />

          <Group justify="flex-end">
            <Button variant="outline" onClick={closeDialog}>
              {t('adminConsole.newConsole.member.cancel')}
            </Button>
            <Button onClick={handleConfirmAssign} disabled={!assigningDept && !departmentOptional}>
              {t('adminConsole.newConsole.member.confirmAssign')}
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Modal>
  );
};
